import { flatMap, omit, uniqBy } from "lodash-es";
import { createSelector } from "reselect";
import { ALLOWED_CRM_ISO_ENDPOINTS } from "components/apps/settings/CrmIsoSetting/helper"; // [crm-iso]
import { flattenPermissionNames } from "utils/permission";
import {
  getAccessiblePhoneNumbers,
  getActivePhoneNumbers,
} from "utils/phoneNumber";

export const configSelector = (state) => state.root.config;
export const appSelector = (state) => state.root.app;
export const apiTokenSelector = (state) => state.root.config.apiToken;
export const loadingInitSelector = (state) => state.root.app.loadingInit;
export const loadedInitSelector = (state) => state.root.app.loadedInit;

export const isMobileViewSelector = (state) => state.root.config.isMobileView;
export const isTabViewSelector = (state) => state.root.config.isTabView;
export const isDesktopLayoutSelector = createSelector(
  isMobileViewSelector,
  isTabViewSelector,
  (isMobileView, isTabView) => !isMobileView && !isTabView,
);
export const isMobileMenuOpenSelector = (state) =>
  state.root.config.isMobileMenuOpen;
export const isMenuCollapseSelector = (state) =>
  state.root.config.isMenuCollapsed;
export const userSelector = (state) => state.root.app.user;
export const selectedCompanySelector = (state) =>
  state.root.config.selectedCompany;
export const companiesSelector = (state) => state.root.app.companies;
export const selectedEndpointSelector = (state) => state.root.config.endpoint;
export const localeSelector = (state) => state.root.config.locale;
export const langSelector = (state) => state.root.app.lang;
export const phoneNumbersSelector = (state) => state.root.app.phone_numbers;
export const phoneNumberStatusesSelector = (state) =>
  state.root.app.phone_number_statuses;
export const agentsSelector = (state) => state.root.app.agents;
export const reviewersSelector = (state) => state.root.app.reviewers;
export const errorCodesSelector = (state) => state.root.app.error_codes;
export const lastStatusLogIdSelector = (state) => state.chat.lastStatusLogId;

export const messagesSelector = createSelector(
  (state) => state.chat.messages,
  (messages) => {
    return uniqBy(messages, "id");
  },
);
export const conversationSelector = (state) => state.chat.conversation;
export const lastMessageIdSelector = (state) => state.chat.lastMessageId;
export const selectedPhoneNumberSelector = (state) =>
  state.root.app.selectedPhoneNumber;
export const templatesSelector = createSelector(
  appSelector,
  selectedPhoneNumberSelector,
  (app, selectedPhoneNumber) => {
    return (
      app.templates.filter(
        (row) => row.phone_number_id === selectedPhoneNumber?.id,
      ) || []
    );
  },
);
export const activeTemplatesSelector = createSelector(
  templatesSelector,
  (templates) => templates.filter((template) => template.is_active !== 0),
);
export const checkTemplatesSelector = createSelector(appSelector, (app) =>
  app.templates.filter((template) => template.is_active),
);
export const allTemplatesSelector = (state) => state.root.app.templates;
export const isAgentOnlineSelector = (state) => state.chat.agent_online;
export const settingMenusSelector = (state) =>
  state.root.app.user?.menus?.settings;
export const typingSelector = (state) => state.chat.typing;
export const optionSelector = (state) => state.root.app.option;
export const tagsSelector = (state) => state.root.app.tags;
export const salutationsSelector = (state) => state.root.app.salutations;
export const announcementSelector = (state) => state.root.app.announcement;
export const permissionsSelector = (state) => state.root.app?.user?.permissions;
// Flat array nama permission (backward-compatible: nested lama ATAU flat baru).
// Memoized supaya flatten hanya jalan saat data permissions berubah.
export const permissionNamesSelector = createSelector(
  permissionsSelector,
  (permissions) => flattenPermissionNames(permissions),
);
export const settingsSelector = createSelector(
  optionSelector,
  (option) => option?.settings || {},
);
// [crm-iso] START — hidden feature (lihat CrmIsoSetting/TEARDOWN.md)
export const crmIsoSettingSelector = (state) =>
  state.root.app.crm_iso_setting || null;
// Fitur CRM ISO aktif untuk user saat ini bila:
// 1) endpoint (company) termasuk yang diizinkan (crmkledo / crmid),
// 2) is_active = true, DAN
// 3) user yang sedang login termasuk dalam daftar agen terpengaruh.
export const isActiveISOSettingSelector = createSelector(
  selectedEndpointSelector,
  userSelector,
  crmIsoSettingSelector,
  (endpoint, user, crmIso) => {
    if (!ALLOWED_CRM_ISO_ENDPOINTS.includes(endpoint)) return false;
    if (!crmIso?.is_active) return false;
    return (crmIso.affected_agents || []).some(
      (agent) => agent.id === user?.id,
    );
  },
);
// crm_iso_setting sudah "settle" (loaded/gagal) untuk endpoint yang diizinkan.
// Untuk endpoint lain langsung dianggap ready (fitur tidak dipakai).
export const crmIsoReadySelector = createSelector(
  selectedEndpointSelector,
  crmIsoSettingSelector,
  (endpoint, crmIso) =>
    !ALLOWED_CRM_ISO_ENDPOINTS.includes(endpoint) || crmIso !== null,
);
// [crm-iso] END
export const categoriesSelectedPhoneNumberSelector = createSelector(
  selectedPhoneNumberSelector,
  (selectedPhoneNumber) => {
    function flattenCategories(items) {
      return flatMap(items, (item) => {
        if (item.categories && item.categories.length > 0) {
          return [
            omit(item, "categories"),
            ...flattenCategories(item.categories),
          ];
        }
        if (!item.categories) return [];
        return [omit(item, "categories")];
      });
    }
    const categories = flattenCategories(selectedPhoneNumber?.categories);
    return categories;
  },
);
export const conversationTypeSelector = (state) => state.chat.conversation_type;
export const pinnedConversationsSelector = (state) =>
  state.chat.pinnedConversations;
export const isAssignModeSelector = createSelector(
  settingsSelector,
  (settings) => {
    return settings.enable_auto_assignment_mode === "1";
  },
);
export const isSupervisorSelector = createSelector(
  selectedPhoneNumberSelector,
  (selectedPhoneNumber) => {
    return selectedPhoneNumber?.roles?.includes(1);
  },
);
export const isAgentSelector = createSelector(
  selectedPhoneNumberSelector,
  (selectedPhoneNumber) => {
    return selectedPhoneNumber?.roles?.includes(2);
  },
);
export const isAgentCanTakeOverConversationSelector = createSelector(
  settingsSelector,
  (settings) => {
    return settings.agents_can_take_over_conversations === "1";
  },
);
export const isAdministratorSelector = createSelector(userSelector, (user) => {
  return user.is_superuser;
});
export const isAgentCanTransferConversationSelector = createSelector(
  settingsSelector,
  (settings) => {
    return settings.agents_can_transfer_conversations === "1";
  },
);
export const isFetchingConversationSelector = (state) =>
  state.chat.isFetchingConversation;

export const pusherConnectedSelector = (state) => state.chat.pusherConnected;

export const isDesktopViewSelector = createSelector(
  configSelector,
  (config) => !config.isMobileView && !config.isTabView,
);

export const websiteIdsSelector = (state) => state.root.app.website_id;

export const chataiResponseSelector = (state) => state.chat.chataiResponse;

export const chataiResponseForCurrentConversationSelector = createSelector(
  (state) => state.chat.chataiResponse,
  conversationSelector,
  (chataiResponse, conversation) => {
    if (!Array.isArray(chataiResponse) || !conversation?.id) return {};
    const forConversation = chataiResponse.filter(
      (item) => item.conversationId === conversation.id,
    );
    return forConversation.length > 0
      ? forConversation[forConversation.length - 1]
      : {};
  },
);

export const topupInformationSelector = createSelector(
  appSelector,
  (app) => app.topup_information || {},
);

export const dataTicketSelector = createSelector(
  appSelector,
  (app) => app.ticket || {},
);
export const inboxTicketSelector = createSelector(
  dataTicketSelector,
  (ticket) => ticket.inboxes || [],
);
export const statusTicketSelector = createSelector(
  dataTicketSelector,
  (ticket) => ticket.statuses || [],
);
export const priorityTicketSelector = createSelector(
  dataTicketSelector,
  (ticket) => ticket.priorities || [],
);
export const filterTicketSelector = createSelector(
  dataTicketSelector,
  (ticket) => ticket.filters || [],
);

export const billingSelector = createSelector(
  appSelector,
  (app) => app?.billing || {},
);

export const inboxTicketingSelector = createSelector(
  dataTicketSelector,
  (ticket) => ticket.inboxes || [],
);

export const activePhoneNumbersSelector = createSelector(
  phoneNumbersSelector,
  phoneNumberStatusesSelector,
  (phoneNumbers, statuses) =>
    getActivePhoneNumbers(phoneNumbers, statuses?.active),
);

export const accessiblePhoneNumbersSelector = createSelector(
  phoneNumbersSelector,
  phoneNumberStatusesSelector,
  isAssignModeSelector,
  isAdministratorSelector,
  (phoneNumbers, statuses, isAssignMode, isAdministrator) =>
    getAccessiblePhoneNumbers({
      phoneNumbers,
      activeStatusId: statuses?.active,
      isAssignMode,
      isAdministrator,
    }),
);

// Tab inbox (conversation_type) yang tersedia di channel terpilih — sama dengan
// pilihan di filterConversation: supervisor/admin → semua tab, agen yang boleh
// ambil alih → my_inbox & unassigned, agen biasa → my_inbox saja.
// Tab inbox yang tersedia untuk user di sebuah channel (roles milik channel tsb).
export const getAvailableConversationTypes = ({
  isSupervisor,
  isAdministrator,
  isAgentCanTakeOver,
}) => {
  if (isSupervisor || isAdministrator) {
    return ["all", "assigned", "my_inbox", "unassigned"];
  }
  if (isAgentCanTakeOver) {
    return ["my_inbox", "unassigned"];
  }
  return ["my_inbox"];
};
export const availableConversationTypesSelector = createSelector(
  isSupervisorSelector,
  isAdministratorSelector,
  isAgentCanTakeOverConversationSelector,
  (isSupervisor, isAdministrator, isAgentCanTakeOver) =>
    getAvailableConversationTypes({
      isSupervisor,
      isAdministrator,
      isAgentCanTakeOver,
    }),
);
